// eslint-disable react-hooks/rules-of-hooks

import {
  Routes,
  Route,
  useNavigate,
  Navigate,
  useLocation,
} from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import CryptoJS from 'crypto-js';
import { APP_SECRET_KEY } from '../../../hooks/constants';

import Home from '../pages/dashboardLayout';
import MyCart from '../pages/cart';
import BuyVPS from '../pages/buyVPS';
import Help from '../../generalPages/loggedInSupport';
import BuyDomain from '../pages/buyDomain';
import Resources from '../pages/resources';
import Activity from '../pages/activity';
import Billing from '../pages/billing';
import Snapshot from '../pages/snapshot';
import ReservedIP from '../pages/reservedIP';
import LoadBalancer from '../pages/loadBalancers';
import ManageProject from '../pages/manageProject';
import Settings from '../pages/settings';
import FAQs from '../pages/faqs';
import ConfigureVPS from '../pages/configureVPS';
import SecurityGroups from '../pages/securityGroups';
import ManageServerSnapshots from '../pages/manageServerSnaphots';
import ManageSecurityGroups from '../pages/manageSecurityGroups';
import Volumes from '../pages/volumes';
import PaymentHistory from '../pages/paymentHistory';
import ResizeInstance from '../pages/resizeInstance';
import ManageVolumeSnapshots from '../pages/manageVolumeSnaphots';
import CreateVolumeSnapshot from '../pages/createVolumeSnaphot';
import Networks from '../pages/networks';
import CreateSubnet from '../pages/createSubnet';
import NetworkDetails from '../pages/networkDetails';
import ServerMetrics from '../pages/serverMetrics';
import Routers from '../pages/routers';
import RouterDetails from '../pages/routerDetails';
import FloatingIPS from '../pages/floatingIPS';
import EditSubnet from '../pages/editSubnet';
import VpnIndexRedirect from '../pages/vpnIndexRedirect';
import VpnIkePolicies from '../pages/vpnIkePolicies';
import VpnIpsecPolicies from '../pages/vpnIpsecPolicies';
import VpnServices from '../pages/vpnServices';
import VpnEndpointGroups from '../pages/vpnEndpointGroups';
import VpnSiteConnections from '../pages/vpnSiteConnections';
import VPN from '../pages/vpn';
import AuditLogs from '../pages/auditLogs';
import ManageLoadBalancers from '../pages/manageLoadBalancers';
import CreateLoadBalancer from '../pages/createLoadBalancer';
import LoadBalancerDetails from '../pages/loadBalancerDetails';
import ListenerDetails from '../pages/listenerDetails';
import PoolDetails from '../pages/poolDetails';
import ManageVolumeBackups from '../pages/manageVolumeBackups';
import CreateVolumeBackup from '../pages/createVolumeBackup';
import CreateVolume from '../pages/createVolume';
import CreateVolumeFromSnapshot from '../pages/createVolumeFromServer';
import ServerSecurityGroups from '../pages/serverSecurityGroups';
import ManagedDatabases from '../pages/managedDatabases';
import EndpointMonitoring from '../pages/endpointMonitoring/EndpointMonitoring';
import EndpointMonitorDetails from '../pages/endpointMonitoring/EndpointMonitorDetails';
import CertificateManager from '../pages/certificateManager';
import BucketStorages from '../pages/bucketStorages';
import BucketPage from '../pages/bucketStorage';

import TopBar from './topBar';
import SideBar from './sideBar';
import WhiteLabelTopBar from './whiteLabelTopBar';
import WhiteLabelSideBar from './whiteLabelSidebar';
import PartnerAdminLayout from './partnerAdminLayout';
import UserSignOut from '../auth/Logout';
import Input from '../../../components/input';
import TextArea from '../../../components/textArea';
import Spinner from '../../../components/spinner';
import {
  listUserProjects,
  updateUserProject,
} from '../../../hooks/local/userReducer';
import { PartnerProvider, usePartner } from '../../../contexts/PartnerContext';
import AutoScaling from '../pages/autoScaling';
import ServerAlarming from '../pages/serverAlarming';
import ResizeObserver from 'resize-observer-polyfill';

const ro = new ResizeObserver(() => {
  try {
    // do nothing
  } catch (e) {}
});

const ignoreResizeObserverError = () => {};
window.ResizeObserver = class ResizeObserver extends window.ResizeObserver {
  constructor(callback) {
    super((...args) => {
      try {
        callback(...args);
      } catch (e) {}
    });
  }
};

const UserLayout = () => {
  const mainRoutesConfig = [
    {
      path: '/client-area',
      element: <Home />,
      children: [
        {
          path: '',
          element: Resources,
        },
        {
          path: 'snapshot',
          element: Snapshot,
        },
        {
          path: 'reservedIP',
          element: ReservedIP,
        },
        {
          path: 'manage',
          element: ManageProject,
        },
        {
          path: 'vps',
          element: BuyVPS,
        },
        {
          path: 'volumes',
          element: Volumes,
        },
        {
          path: 'configure-vps',
          element: ConfigureVPS,
        },
      ],
    },
    {
      path: '/billing',
      element: Billing,
    },
    {
      path: '/event-logs',
      element: AuditLogs,
    },
    {
      path: '/routers',
      element: Routers,
    },
    {
      path: '/routers/router-details',
      element: RouterDetails,
    },
    {
      path: '/networks',
      element: Networks,
    },
    // VPN main page (OpenStack-style tabs)
    { path: '/networks/vpn', element: VPN },

    // VPN under Networks (sidebar expects these)
    { path: '/networks/vpn/ike-policies', element: VpnIkePolicies },
    { path: '/networks/vpn/ipsec-policies', element: VpnIpsecPolicies },
    { path: '/networks/vpn/services', element: VpnServices },
    { path: '/networks/vpn/endpoint-groups', element: VpnEndpointGroups },
    { path: '/networks/vpn/site-connections', element: VpnSiteConnections },

    // Also support shorter direct paths
    { path: '/vpn', element: VpnIndexRedirect },
    { path: '/vpn/ike-policies', element: VpnIkePolicies },
    { path: '/vpn/ipsec-policies', element: VpnIpsecPolicies },
    { path: '/vpn/services', element: VpnServices },
    { path: '/vpn/endpoint-groups', element: VpnEndpointGroups },
    { path: '/vpn/site-connections', element: VpnSiteConnections },
    {
      path: '/networks/network-details',
      element: NetworkDetails,
    },
    {
      path: '/resources/:id/servers/:id2',
      element: ServerMetrics,
    },
    {
      path: '/resources/:id/endpoint-monitoring',
      element: EndpointMonitoring,
    },
    {
      path: '/resources/:id/endpoint-monitoring/:monitorId',
      element: EndpointMonitorDetails,
    },
    {
      path: '/:id/servers/:id2/server-alarming',
      element: ServerAlarming,
    },
    {
      path: '/resources/:id/auto-scaling',
      element: AutoScaling,
    },
    {
      path: '/resources/:id/bucket-storages',
      element: BucketStorages,
    },
    {
      path: '/resources/:id/bucket-storages/:id2',
      element: BucketPage,
    },
    {
      path: '/networks/create-subnet',
      element: CreateSubnet,
    },
    {
      path: '/networks/edit-subnet',
      element: EditSubnet,
    },
    {
      path: '/floating-ips',
      element: FloatingIPS,
    },
    {
      path: '/createLoadBalancer',
      element: CreateLoadBalancer,
    },
    {
      path: '/manageLoadBalancers',
      element: ManageLoadBalancers,
    },
    {
      path: '/managedDatabases',
      element: ManagedDatabases,
    },
    {
      path: '/manageLoadBalancers/loadBalancer-details',
      element: LoadBalancerDetails,
    },
    {
      path: '/manageLoadBalancers/listener-details',
      element: ListenerDetails,
    },
    {
      path: '/manageLoadBalancers/pool-details',
      element: PoolDetails,
    },
    {
      path: '/manageServerSnapshots',
      element: ManageServerSnapshots,
    },
    {
      path: '/certificate-manager',
      element: CertificateManager,
    },
    {
      path: '/createVolumeSnapshot',
      element: CreateVolumeSnapshot,
    },
    {
      path: '/createVolume',
      element: CreateVolume,
    },
    {
      path: '/createVolumeFromSnapshot',
      element: CreateVolumeFromSnapshot,
    },
    {
      path: '/createVolumeBackup',
      element: CreateVolumeBackup,
    },
    {
      path: '/manageVolumeSnapshots',
      element: ManageVolumeSnapshots,
    },
    {
      path: '/manageVolumeBackups',
      element: ManageVolumeBackups,
    },
    {
      path: '/resizeInstance',
      element: ResizeInstance,
    },
    {
      path: '/payment-history',
      element: PaymentHistory,
    },
    {
      path: '/cart',
      element: MyCart,
    },
    {
      path: '/securityGroups',
      element: SecurityGroups,
    },
    {
      path: '/serverSecurityGroups',
      element: ServerSecurityGroups,
    },
    {
      path: '/manageSecurityGroups/:id',
      element: ManageSecurityGroups,
    },
    {
      path: '/loadBalancer',
      element: LoadBalancer,
    },
    {
      path: '/domain',
      element: BuyDomain,
    },
    {
      path: '/help',
      element: Help,
    },
    {
      path: '/settings',
      element: Settings,
    },
    {
      path: '/faq',
      element: FAQs,
    },
    {
      path: '/sign-out',
      element: UserSignOut,
    },
    {
      path: '*',
      element: () => <Navigate to="/client-area" replace />,
    },
  ];

  const partnerRoutesConfig = [
    {
      path: '/client-area',
      element: Home,
      children: [
        {
          path: '',
          element: Resources,
        },
        {
          path: 'snapshot',
          element: Snapshot,
        },
        {
          path: 'reservedIP',
          element: ReservedIP,
        },
        {
          path: 'manage',
          element: ManageProject,
        },
        {
          path: 'vps',
          element: BuyVPS,
        },
        {
          path: 'volumes',
          element: Volumes,
        },
        {
          path: 'configure-vps',
          element: ConfigureVPS,
        },
      ],
    },
    {
      path: '/billing',
      element: Billing,
    },
    {
      path: '/event-logs',
      element: AuditLogs,
    },
    {
      path: '/resources/:id/servers/:id2',
      element: ServerMetrics,
    },
    {
      path: '/:id/servers/:id2/server-alarming',
      element: ServerAlarming,
    },
    {
      path: '/resources/:id/auto-scaling',
      element: AutoScaling,
    },
    {
      path: '/resources/:id/bucket-storages',
      element: BucketStorages,
    },
    {
      path: '/resources/:id/bucket-storages/:id2',
      element: BucketPage,
    },
    {
      path: '/createLoadBalancer',
      element: CreateLoadBalancer,
    },
    {
      path: '/manageLoadBalancers',
      element: ManageLoadBalancers,
    },
    {
      path: '/resources/:id/endpoint-monitoring',
      element: EndpointMonitoring,
    },
    {
      path: '/resources/:id/endpoint-monitoring/:monitorId',
      element: EndpointMonitorDetails,
    },
    {
      path: '/managedDatabases',
      element: ManagedDatabases,
    },
    {
      path: '/manageLoadBalancers/loadBalancer-details',
      element: LoadBalancerDetails,
    },
    {
      path: '/manageLoadBalancers/listener-details',
      element: ListenerDetails,
    },
    {
      path: '/manageLoadBalancers/pool-details',
      element: PoolDetails,
    },
    {
      path: '/manageServerSnapshots',
      element: ManageServerSnapshots,
    },
    {
      path: '/certificate-manager',
      element: CertificateManager,
    },
    {
      path: '/createVolumeSnapshot',
      element: CreateVolumeSnapshot,
    },
    {
      path: '/createVolume',
      element: CreateVolume,
    },
    {
      path: '/createVolumeFromSnapshot',
      element: CreateVolumeFromSnapshot,
    },
    {
      path: '/createVolumeBackup',
      element: CreateVolumeBackup,
    },
    {
      path: '/manageVolumeSnapshots',
      element: ManageVolumeSnapshots,
    },
    {
      path: '/manageVolumeBackups',
      element: ManageVolumeBackups,
    },
    {
      path: '/resizeInstance',
      element: ResizeInstance,
    },
    {
      path: '/payment-history',
      element: PaymentHistory,
    },
    {
      path: '/cart',
      element: MyCart,
    },
    {
      path: '/domain',
      element: BuyDomain,
    },
    {
      path: '/help',
      element: Help,
    },
    {
      path: '/settings',
      element: Settings,
    },
    {
      path: '/faq',
      element: FAQs,
    },
    {
      path: '/sign-out',
      element: UserSignOut,
    },
    {
      path: '*',
      element: () => <Navigate to="/client-area" replace />,
    },
  ];

  const renderRoutes = (routes) => {
    return routes.map((route, index) => {
      if (route.children) {
        return (
          <Route key={index} path={route.path} element={route.element}>
            {route.children.map((child, childIndex) => (
              <Route
                key={`${index}-${childIndex}`}
                path={child.path}
                element={<child.element />}
              />
            ))}
          </Route>
        );
      }

      return (
        <Route key={index} path={route.path} element={<route.element />} />
      );
    });
  };

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    isWhiteLabel,
    isPartnerAdmin,
    loading: partnerLoading,
  } = usePartner();

  const userSessionData = useSelector((state) => state.user.userSessionData);
  const userProjectList = useSelector((state) => state.user.userProjectList);
  const userLoading = useSelector((state) => state.user).loading;
  const isAuthenticated = useSelector((state) => state.user.isAuthenticated);
  const [showDefaultProjectDiv, setDefaultProjectDiv] = useState(false);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const updateDefaultProject = useFormik({
    enableReinitialize: true,
    initialValues: {
      projectId: userProjectList?.[0]?.id || '',
      projectDescription: '',
      projectName: '',
    },
    validationSchema: Yup.object({
      projectName: Yup.string().required('Project name cannot be empty'),
      projectDescription: Yup.string().required(
        'Project description cannot be empty',
      ),
    }),
    onSubmit: async (values, { resetForm }) => {
      const { projectId, projectName, projectDescription } = values;
      let updateProjectData = {
        name: projectName,
        description: projectDescription,
        projectId,
      };
      const { payload } = await dispatch(
        updateUserProject({ updateProjectData, navigate }),
      );
      if (payload.statuscode === '00') {
        await dispatch(listUserProjects());
        setDefaultProjectDiv(false);
        navigate('/client-area');
        resetForm();
      }
    },
  });

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (
      location.pathname !== '/client-area/vps' &&
      location.pathname !== '/client-area/configure-vps'
    ) {
      const selectedSnapshot = JSON.parse(
        sessionStorage.getItem('selectedServerSnapshot'),
      );
      if (selectedSnapshot && selectedSnapshot.action === 'launch') {
        sessionStorage.removeItem('selectedServerSnapshot');
        
      }
    }
  }, [location.pathname]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    if (userProjectList?.length > 0 && userSessionData?.email) {
      const userDefaultProjectName = userProjectList[0]?.name;
      if (userDefaultProjectName === userSessionData.email) {
        setDefaultProjectDiv(true);
      }
    }
  }, [userProjectList, userSessionData]);

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    const checkAuth = () => {
      if (isAuthenticated) return true;

      const encryptedData = localStorage.getItem('userSessionData');
      if (encryptedData) {
        try {
          const bytes = CryptoJS.AES.decrypt(encryptedData, APP_SECRET_KEY);
          const decryptedData = JSON.parse(bytes.toString(CryptoJS.enc.Utf8));
          if (decryptedData) return true;
        } catch (e) {
          return false;
        }
      }
      return false;
    };

    if (!checkAuth()) {
      navigate('/sign-in');
    }
  }, [isAuthenticated, navigate]);

  if (isPartnerAdmin) {
    return <PartnerAdminLayout />;
  }

  if (!isAuthenticated) {
    return <Spinner loading={true} />;
  }

  const TopBarComponent = isWhiteLabel ? WhiteLabelTopBar : TopBar;
  const SideBarComponent = isWhiteLabel ? WhiteLabelSideBar : SideBar;
  const appRoutesConfig = isWhiteLabel ? partnerRoutesConfig : mainRoutesConfig;

  return (
    <div className="grid items-start relative">
      <Spinner loading={partnerLoading || userLoading} />
      {showDefaultProjectDiv && (
        <div className="fixed top-0 h-screen w-full z-50 bg-black/60 backdrop-blur-md flex items-center justify-center p-4">
          <div className="bg-white dark:bg-[#141D26] rounded-md p-4 md:p-10 w-full md:w-1/3">
            <p className="text-xl text-[#1DA1F2]">
              Welcome to your CloudVantage Dashboard!
            </p>
            <p className="text-sm text-gray-500 mt-2">
              Your account comes with a default project. Update the details
              before continuing to your dashboard.
            </p>
            <form onSubmit={updateDefaultProject.handleSubmit}>
              <div className="grid gap-4 mt-8">
                <Input
                  label={'Project name'}
                  type={'text'}
                  name={'projectName'}
                  value={updateDefaultProject.values.projectName}
                  onBlur={updateDefaultProject.handleBlur}
                  onChange={updateDefaultProject.handleChange}
                  onError={
                    updateDefaultProject.errors.projectName &&
                    updateDefaultProject.touched.projectName
                      ? updateDefaultProject.errors.projectName
                      : null
                  }
                />
                <TextArea
                  label={'Project description'}
                  type={'text'}
                  noOfRow={'3'}
                  name={'projectDescription'}
                  value={updateDefaultProject.values.projectDescription}
                  onBlur={updateDefaultProject.handleBlur}
                  onChange={updateDefaultProject.handleChange}
                  onError={
                    updateDefaultProject.errors.projectDescription &&
                    updateDefaultProject.touched.projectDescription
                      ? updateDefaultProject.errors.projectDescription
                      : null
                  }
                />
                <div className="grid">
                  <button
                    type="submit"
                    className="text-sm md:text-xs opacity-70 text-white bg-[#1DA1F2] rounded px-8 py-4 cursor-pointer"
                  >
                    Update project details
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
      <div className="w-full sticky top-0 z-40">
        <TopBarComponent />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-12">
        <SideBarComponent />
        <div className="lg:col-span-10">
          <Routes>{renderRoutes(appRoutesConfig)}</Routes>
        </div>
      </div>
    </div>
  );
};

const WrappedUserLayout = () => (
  <PartnerProvider>
    <UserLayout />
  </PartnerProvider>
);

export default WrappedUserLayout;
